import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Field, Input, Select } from '@/components/ui/input';
import { EmptyState, PageHeader } from '@/components/ui/misc';

/**
 * Consulta del arancel aduanero (TARIC).
 *
 * Dos filtros que se combinan con la descripción: el **código** navega la nomenclatura por prefijo
 * (70 → 7013 → 7013 42) y el **texto** busca en la descripción en español, con o sin tildes. El país
 * de origen no filtra la lista: cambia los derechos que se enseñan al abrir un código, porque el mismo
 * artículo paga distinto según de dónde venga (China con antidumping, Turquía sin él).
 */

interface Codigo {
  code: string; level: number; description: string; parentCode: string | null;
}

interface Medida {
  id: number; code: string; addCode: string | null; origin: string | null; originCode: string | null;
  measure: string; dutyKind: string; rate: number | null; dutyText: string;
  validFrom: string | null; validTo: string | null;
}

interface Detalle {
  code: string; level: number; description: string; descriptionEn: string; parentCode: string | null;
  queryDate: string;
  summary: { measure: string; origin: string | null; rate: number; code: string }[];
  truncated: boolean;
  groupPreferences: number;
  measures: Medida[];
}

interface Estado {
  loaded: boolean; publishedAt?: string; codes?: number; measures?: number;
}

const MEDIDAS_ES: Record<string, string> = {
  'Third country duty': 'Derecho de terceros países',
  'Definitive anti-dumping duty': 'Derecho antidumping definitivo',
  'Tariff preference': 'Preferencia arancelaria',
  'Non preferential tariff quota': 'Contingente arancelario no preferencial',
  'Preferential tariff quota': 'Contingente arancelario preferencial',
  'Import control': 'Control de importación'
};

export function TariffPage() {
  const [texto, setTexto] = useState('');
  const [codigo, setCodigo] = useState('');
  const [origen, setOrigen] = useState('');
  const [abierto, setAbierto] = useState<string | null>(null);

  const estado = useQuery({ queryKey: ['tariff', 'status'], queryFn: () => api<{ data: Estado }>('/tariff/status') });

  const paises = useQuery({
    // Clave propia: `useGeo` guarda en `['geo','countries','v2']` una forma distinta.
    queryKey: ['tariff', 'countries'],
    queryFn: () => api<{ data: { code: string; nameEs: string }[] }>('/geo/countries'),
    staleTime: 24 * 60 * 60 * 1000
  });

  const busqueda = useQuery({
    queryKey: ['tariff', 'codes', texto, codigo],
    queryFn: () => api<{ data: Codigo[] }>(`/tariff/codes?${new URLSearchParams({
      ...(texto.trim() ? { q: texto.trim() } : {}),
      ...(codigo.trim() ? { code: codigo.trim() } : {}),
      limit: '50'
    })}`),
    enabled: Boolean(texto.trim() || codigo.trim())
  });

  const detalle = useQuery({
    queryKey: ['tariff', 'code', abierto, origen],
    queryFn: () => api<{ data: Detalle }>(`/tariff/codes/${abierto}?${new URLSearchParams(origen ? { origin: origen } : {})}`),
    enabled: Boolean(abierto)
  });

  const filas = busqueda.data?.data ?? [];
  const cargado = estado.data?.data.loaded;

  const porMedida = useMemo(() => {
    const grupo = new Map<string, Medida[]>();
    for (const m of detalle.data?.data.measures ?? []) {
      const lista = grupo.get(m.measure) ?? [];
      lista.push(m);
      grupo.set(m.measure, lista);
    }
    return [...grupo.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [detalle.data]);

  return (
    <div className="space-y-3">
      <PageHeader
        title="Arancel (TARIC)"
        description="Consulta la nomenclatura y los derechos de importación que aplican a cada código."
        actions={cargado ? (
          <Badge tone="neutral">
            Catálogo de {estado.data?.data.publishedAt} · {estado.data?.data.codes?.toLocaleString('es-ES')} códigos
          </Badge>
        ) : (
          <Badge tone="warning">Catálogo sin cargar</Badge>
        )}
      />

      {!cargado ? (
        <Card className="p-3 text-[12px] text-muted-foreground">
          El catálogo del arancel todavía no está cargado en esta instalación. Se carga con
          <code className="mx-1 rounded bg-muted px-1">npm run taric:load</code>
          (o desde Administración → Arancel cuando esté disponible la actualización).
        </Card>
      ) : null}

      <Card className="space-y-3 p-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Descripción">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-8" value={texto} onChange={(e) => setTexto(e.target.value)}
                placeholder="vidrio, espejo, aluminio, tornillo…" />
            </div>
          </Field>
          <Field label="Código" hint="Prefijo: 70, 7013, 701342">
            <Input value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="7013" />
          </Field>
          <Field label="País de origen" hint="Cambia los derechos que se enseñan">
            <Select value={origen} onChange={(e) => setOrigen(e.target.value)}>
              <option value="">Todos / ERGA OMNES</option>
              {(paises.data?.data ?? []).map((p) => (
                <option key={p.code} value={p.code}>{p.nameEs}</option>
              ))}
            </Select>
          </Field>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Varias palabras se exigen todas, en cualquier orden, y no hacen falta tildes. Sin criterio se
          listan los capítulos, para bajar por la nomenclatura.
        </p>
      </Card>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <Card className="divide-y p-0">
          {filas.length === 0 ? (
            <EmptyState
              title={busqueda.isFetching ? 'Buscando…' : 'Sin resultados'}
              description={busqueda.isFetching ? 'Consultando el catálogo' : 'Prueba con otra palabra o con un prefijo de código'}
            />
          ) : filas.map((f) => (
            <button
              key={f.code}
              type="button"
              onClick={() => setAbierto(f.code)}
              className={`flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-muted/60 ${abierto === f.code ? 'bg-muted/80' : ''}`}
            >
              <span className="w-24 shrink-0 font-mono text-[12px] text-muted-foreground">{f.code}</span>
              <span className="text-[12px]">{f.description}</span>
            </button>
          ))}
        </Card>

        <Card className="space-y-3 p-3">
          {!abierto ? (
            <EmptyState title="Elige un código" description="A la izquierda tienes la nomenclatura; al abrir un código salen sus derechos" />
          ) : detalle.isLoading ? (
            <p className="text-[12px] text-muted-foreground">Cargando…</p>
          ) : detalle.data ? (
            <>
              <div>
                <p className="font-mono text-[12px] text-muted-foreground">{detalle.data.data.code}</p>
                <h3 className="text-sm font-semibold">{detalle.data.data.description}</h3>
                <p className="mt-1 text-[11px] text-muted-foreground">{detalle.data.data.descriptionEn}</p>
              </div>
              {detalle.data.data.summary.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {detalle.data.data.summary.map((s, i) => (
                    <Badge key={i} tone={s.measure.includes('anti-dumping') ? 'danger' : 'neutral'}>
                      {MEDIDAS_ES[s.measure] ?? s.measure}: {s.rate}%
                    </Badge>
                  ))}
                </div>
              ) : null}
              {detalle.data.data.truncated ? (
                <p className="rounded-md bg-amber-500/10 px-2 py-1 text-[11px]">
                  Este código tiene muchos derechos: se enseñan los más relevantes. Afina por país de origen.
                </p>
              ) : null}
              {detalle.data.data.groupPreferences > 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  Hay {detalle.data.data.groupPreferences} preferencias por grupo de países (SPG y similares) que
                  no se evalúan aquí: compruébalas con el código adicional de la declaración.
                </p>
              ) : null}
              <p className="text-[11px] text-muted-foreground">
                Derechos vigentes el {detalle.data.data.queryDate}
                {origen ? ' para el origen elegido' : ''}.
              </p>
              <div className="space-y-3">
                {porMedida.map(([medida, lista]) => (
                  <div key={medida}>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {MEDIDAS_ES[medida] ?? medida}
                    </p>
                    <ul className="mt-1 space-y-1">
                      {lista.slice(0, 12).map((m) => (
                        <li key={m.id} className="flex items-baseline justify-between gap-2 text-[12px]">
                          <span className="truncate">
                            {m.origin ?? 'Todos los orígenes'}
                            {m.addCode ? <span className="ml-1 font-mono text-[10px] text-muted-foreground">+{m.addCode}</span> : null}
                            {/* Un antidumping puede valer solo para una subdivisión de 10 dígitos. */}
                            {m.code && m.code !== detalle.data?.data.code ? (
                              <span className="ml-1 font-mono text-[10px] text-muted-foreground">{m.code}</span>
                            ) : null}
                          </span>
                          <span className="shrink-0 font-mono">{m.dutyText}</span>
                        </li>
                      ))}
                      {lista.length > 12 ? (
                        <li className="text-[11px] text-muted-foreground">y {lista.length - 12} más…</li>
                      ) : null}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-[12px] text-destructive">No se pudo cargar el código</p>
          )}
        </Card>
      </div>
    </div>
  );
}
